// Hooks compartilhados: sessao, relogio do contest e SSE.

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ContestStateDto,
  ScoreboardDto,
  SessionUser,
  SubmissionEventDto,
} from "@compila/shared";
import { api, ApiError } from "./api.js";

/* ------------------------------------------------------------------ */
/* Sessao                                                              */
/* ------------------------------------------------------------------ */

export function useSession() {
  return useQuery<SessionUser | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<SessionUser>("/me");
      } catch (err) {
        // 401 nao e falha: e "ninguem logado". Devolver null evita a tela de
        // erro no lugar da tela de login.
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

/* ------------------------------------------------------------------ */
/* Relogio do contest                                                  */
/* ------------------------------------------------------------------ */

/**
 * Cronometro que avanca sozinho.
 *
 * O servidor manda `elapsedSeconds` e `serverNow`; guardamos a diferenca entre
 * o relogio dele e o daqui e somamos a cada segundo. O relogio da maquina da
 * equipe nunca decide nada — ele so desenha entre uma atualizacao e outra.
 */
export function useContestClock(contest: ContestStateDto | undefined): number {
  const [elapsed, setElapsed] = useState(0);
  const base = useRef<{ elapsed: number; at: number } | null>(null);

  useEffect(() => {
    if (!contest) return;
    const skew = Date.now() - new Date(contest.serverNow).getTime();
    base.current = { elapsed: contest.elapsedSeconds, at: Date.now() - skew };
    setElapsed(contest.elapsedSeconds);
  }, [contest?.elapsedSeconds, contest?.serverNow, contest?.status]);

  useEffect(() => {
    if (!contest || contest.status !== "RUNNING") return;
    const timer = setInterval(() => {
      const anchor = base.current;
      if (!anchor) return;
      const seconds = anchor.elapsed + Math.floor((Date.now() - anchor.at) / 1000);
      setElapsed(Math.min(seconds, contest.durationSeconds));
    }, 1000);
    return () => clearInterval(timer);
  }, [contest?.status, contest?.durationSeconds]);

  return elapsed;
}

/* ------------------------------------------------------------------ */
/* SSE                                                                 */
/* ------------------------------------------------------------------ */

export type ConnectionState = "connecting" | "open" | "closed";

export interface StreamHandlers {
  onScoreboard?: (board: ScoreboardDto) => void;
  onContest?: (contest: ContestStateDto) => void;
  onSubmission?: (event: SubmissionEventDto) => void;
}

/**
 * Assina um endpoint SSE.
 *
 * O EventSource reconecta sozinho. Ao reconectar, refazemos as consultas do
 * TanStack Query para ressincronizar o que tiver mudado enquanto a conexao
 * estava fora (secao 9 da especificacao).
 */
export function useEventStream(url: string, handlers: StreamHandlers): ConnectionState {
  const [state, setState] = useState<ConnectionState>("connecting");
  const queryClient = useQueryClient();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const everConnected = useRef(false);

  useEffect(() => {
    const source = new EventSource(url, { withCredentials: true });

    source.onopen = () => {
      setState("open");
      if (everConnected.current) {
        // Reconexao: pode ter havido veredito enquanto estavamos fora.
        void queryClient.invalidateQueries();
      }
      everConnected.current = true;
    };

    source.onerror = () => {
      // O EventSource ja reconecta sozinho; so refletimos na interface.
      setState("connecting");
    };

    const listen = <T,>(event: string, handler: (data: T) => void): void => {
      source.addEventListener(event, (message) => {
        try {
          handler(JSON.parse((message as MessageEvent<string>).data) as T);
        } catch {
          // Evento malformado nao pode derrubar a tela inteira.
        }
      });
    };

    listen<ScoreboardDto>("scoreboard", (d) => handlersRef.current.onScoreboard?.(d));
    listen<ContestStateDto>("contest", (d) => handlersRef.current.onContest?.(d));
    listen<SubmissionEventDto>("submission", (d) =>
      handlersRef.current.onSubmission?.(d),
    );

    return () => {
      source.close();
      setState("closed");
    };
  }, [url, queryClient]);

  return state;
}

/* ------------------------------------------------------------------ */
/* Cooldown de submissao                                               */
/* ------------------------------------------------------------------ */

/** Contagem regressiva do cooldown, para desabilitar o botao de enviar. */
export function useCooldown(): {
  remaining: number;
  start: (seconds: number) => void;
} {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  const start = useCallback((seconds: number) => {
    setRemaining(Math.max(0, Math.ceil(seconds)));
  }, []);

  return { remaining, start };
}
